const LINE_ITEM_COMPARE_FIELDS = [
  'gl_code_1',
  'gl_code_2',
  'gl_code_3',
  'gl_code_4',
  'gl_code_5',
  'amount',
  'identifier',
  'memo',
  'internal_comment',
];

const emptyLineItem = () => ({
  gl_code_1: '',
  gl_code_2: '',
  gl_code_3: '',
  gl_code_4: '',
  gl_code_5: '',
  amount: '',
  identifier: '',
  memo: '',
  internal_comment: '',
});

const normalizeValue = (value) => (value == null ? '' : String(value).trim());

export const extractLineItemsFromInfo = (info) => {
  if (
    info == null ||
    !Object.prototype.hasOwnProperty.call(info, 'line_items')
  ) {
    return null;
  }

  if (!Array.isArray(info.line_items)) {
    return null;
  }

  return info.line_items.map((row) => {
    const source = row && typeof row === 'object' ? row : {};
    return {
      ...emptyLineItem(),
      gl_code_1: normalizeValue(source.gl_code_1),
      gl_code_2: normalizeValue(source.gl_code_2),
      gl_code_3: normalizeValue(source.gl_code_3),
      gl_code_4: normalizeValue(source.gl_code_4),
      gl_code_5: normalizeValue(source.gl_code_5),
      amount: normalizeValue(source.amount),
      identifier: normalizeValue(source.identifier),
      memo: normalizeValue(source.memo),
      internal_comment: normalizeValue(source.internal_comment),
    };
  });
};

export const mapCurrentLineItems = (rows) =>
  extractLineItemsFromInfo({ line_items: rows });

export const findLatestRefundInfo = (infoHistory) =>
  infoHistory.find((info) => info.type === 'refund_info');

export const needsCurrentLineItems = (latestRefundInfo) =>
  !latestRefundInfo ||
  extractLineItemsFromInfo(latestRefundInfo.new_info) === null;

export const resolveHistoryLineItems = (
  info,
  latestRefundInfo,
  currentLineItems,
) =>
  extractLineItemsFromInfo(info.new_info) ??
  (info.id === latestRefundInfo?.id ? currentLineItems : null);

export const getChangedLineItemFields = (current, previous) => {
  const changed = new Set();
  for (const field of LINE_ITEM_COMPARE_FIELDS) {
    if (
      normalizeValue(current?.[field]) !== normalizeValue(previous?.[field])
    ) {
      changed.add(field);
    }
  }
  return changed;
};

/** Blank out fields that are hidden in the UI so they do not affect diff status. */
export const sanitizeLineItemsForHistoryCompare = (
  lineItems,
  visibleFieldKeys,
) => {
  if (lineItems == null) {
    return null;
  }

  const visible = new Set(visibleFieldKeys);
  return lineItems.map((item) => {
    const sanitized = { ...item };
    for (const field of LINE_ITEM_COMPARE_FIELDS) {
      if (!visible.has(field)) {
        sanitized[field] = '';
      }
    }
    return sanitized;
  });
};

/**
 * Build display rows for a history snapshot vs the next-older snapshot.
 * `previousLineItems === null` means the older snapshot did not record line items.
 */
export const buildLineItemHistoryRows = (lineItems, previousLineItems) => {
  // Missing `line_items` on a snapshot means it was not recorded — do not invent diffs.
  if (lineItems == null) {
    return [];
  }

  const previous = previousLineItems ?? [];
  const rows = [];

  lineItems.forEach((item, index) => {
    const previousItem = previous[index];
    if (previousItem == null) {
      rows.push({
        item,
        status: previousLineItems == null ? 'unchanged' : 'added',
        changedFields: new Set(),
      });
      return;
    }

    const changedFields = getChangedLineItemFields(item, previousItem);
    rows.push({
      item,
      status: changedFields.size > 0 ? 'changed' : 'unchanged',
      changedFields,
    });
  });

  if (previousLineItems != null) {
    for (
      let index = lineItems.length;
      index < previousLineItems.length;
      index += 1
    ) {
      rows.push({
        item: previousLineItems[index],
        status: 'removed',
        changedFields: new Set(),
      });
    }
  }

  return rows;
};

/** Previous refund_info snapshot in the flattened history list (newer → older). */
export const getPreviousRefundInfoLineItems = (infoHistory, index) => {
  for (let i = index + 1; i < infoHistory.length; i += 1) {
    const previous = infoHistory[i];
    if (previous?.type === 'refund_info') {
      return previous.line_items ?? null;
    }
  }
  return null;
};
